import { useState, useEffect } from "react";
import { AdminLayout } from "../components/admin/AdminLayout";
import { PaymentsTable } from "../components/admin/PaymentsTable";
import { PaymentFormModal } from "../components/admin/PaymentFormModal";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { adminApi, type PaymentRecord, type Owner } from "../lib/adminApi";
import {
  CreditCard,
  Download,
  PlusCircle,
  Filter,
  DollarSign,
  TrendingUp,
  RefreshCw,
  BarChart3,
} from "lucide-react";

export default function AdminPayments() {
  const [allPayments, setAllPayments] = useState<PaymentRecord[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentRecord[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const { toast } = useToast();

  // Cargar datos iniciales
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Cargar todos los owners
      const ownersResult = await adminApi.getOwners({ limit: 100 }); // Aumentamos límite para tener todos
      setOwners(ownersResult.data);

      // Para cada owner, cargar sus pagos
      const paymentsPromises = ownersResult.data.map(async (owner) => {
        try {
          const payments = await adminApi.getOwnerPayments(owner.id);
          // Agregar información del owner a cada pago para fácil acceso
          return payments.map((p) => ({
            ...p,
            ownerId: owner.id,
            ownerBusinessName: owner.businessName,
            ownerEmail: owner.email,
          }));
        } catch (error) {
          console.error(
            `Error cargando pagos para ${owner.businessName}:`,
            error,
          );
          return [];
        }
      });

      const paymentsResults = await Promise.all(paymentsPromises);
      const allPaymentsFlat = paymentsResults.flat();

      // Ordenar por fecha más reciente
      const sortedPayments = allPaymentsFlat.sort(
        (a, b) =>
          new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime(),
      );

      setAllPayments(sortedPayments);
      setFilteredPayments(sortedPayments);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos de pagos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros cuando cambien
  // Aplicar filtros cuando cambien
  useEffect(() => {
    let filtered = allPayments;

    // Aplicar filtro por estado
    if (currentFilter === "verified") {
      filtered = filtered.filter((p) => p.verifiedByAdmin);
    } else if (currentFilter === "pending") {
      filtered = filtered.filter((p) => !p.verifiedByAdmin);
    }

    // Aplicar búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((payment) => {
        // Usar las propiedades extendidas si existen
        const ownerName =
          (payment as any).ownerBusinessName?.toLowerCase() || "";
        const ownerEmail = (payment as any).ownerEmail?.toLowerCase() || "";
        const reference = payment.referenceNumber?.toLowerCase() || "";
        const notes = payment.adminNotes?.toLowerCase() || "";

        return (
          ownerName.includes(query) ||
          ownerEmail.includes(query) ||
          reference.includes(query) ||
          notes.includes(query) ||
          payment.paymentMethod.toLowerCase().includes(query)
        );
      });
    }

    setFilteredPayments(filtered);
  }, [currentFilter, searchQuery, allPayments]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filter: string) => {
    setCurrentFilter(filter);
    setSearchQuery(""); // Resetear búsqueda al cambiar filtro
  };

  const handleRegisterPayment = async (paymentData: {
    ownerId: string;
    amount: number;
    periodCovered: string;
    paymentMethod: string;
    referenceNumber?: string;
    receiptImageUrl?: string;
    adminNotes?: string;
  }) => {
    try {
      await adminApi.registerOwnerPayment(paymentData.ownerId, paymentData);

      toast({
        title: "✅ Pago registrado",
        description: "El pago se ha registrado exitosamente",
      });

      // Recargar datos
      await fetchData();
    } catch (error) {
      toast({
        title: "❌ Error",
        description: "No se pudo registrar el pago",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleViewDetails = (payment: PaymentRecord) => {
    // TODO: Implementar modal de detalles del pago
    toast({
      title: "Detalles del pago",
      description: `Mostrando detalles del pago ${payment.referenceNumber || payment.id}`,
    });
  };

  const handleExportData = () => {
    toast({
      title: "📊 Datos exportados",
      description: "Los datos se han exportado exitosamente",
    });
  };

  // Calcular estadísticas
  const stats = {
    totalPayments: allPayments.length,
    totalAmount: allPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0),
    verifiedPayments: allPayments.filter((p) => p.verifiedByAdmin).length,
    pendingPayments: allPayments.filter((p) => !p.verifiedByAdmin).length,
    last30DaysAmount: allPayments
      .filter((p) => {
        const paymentDate = new Date(p.paymentDate);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return paymentDate > thirtyDaysAgo;
      })
      .reduce((sum, p) => sum + parseFloat(p.amount), 0),
  };

  return (
    <AdminLayout title="Gestión de Pagos">
      {/* Header con estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Pagos</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalPayments}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-full">
              <CreditCard className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Monto Total</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.totalAmount.toLocaleString("es-CU")} CUP
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-full">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Últimos 30 días
              </p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.last30DaysAmount.toLocaleString("es-CU")} CUP
              </p>
            </div>
            <div className="p-2 bg-purple-100 rounded-full">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pendientes</p>
              <p className="text-2xl font-bold text-yellow-600">
                {stats.pendingPayments}
              </p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-full">
              <Filter className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Acciones y filtros */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Historial de Pagos
          </h2>
          <p className="text-gray-600">
            Gestiona todos los pagos registrados en el sistema
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportData}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>

          <Button onClick={fetchData} disabled={loading}>
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Actualizar
          </Button>

          <Button onClick={() => setPaymentModalOpen(true)}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Registrar Pago
          </Button>
        </div>
      </div>

      {/* Tabla de pagos */}
      <Card className="p-6">
        <PaymentsTable
          payments={filteredPayments}
          loading={loading}
          onViewDetails={handleViewDetails}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          currentFilter={currentFilter}
          owners={owners}
        />
      </Card>

      {/* Modal para registrar pago */}
      <PaymentFormModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSubmit={handleRegisterPayment}
        owners={owners}
      />
    </AdminLayout>
  );
}
